import axios from 'axios';

import API_URL from '../../config';

export class PagoService {

    /**
     * Obtener historial de pagos global o filtrado
     * Endpoint: GET /api/v2/pagos
     */
    static async getHistorialPagos(filters = {}) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            // Mapeamos filtros de frontend a lo que espera el backend
            const params = {
                page: filters.page || 1,
                limit: filters.limit || 10,
                estado: filters.estado,
                nombre: filters.nombreCliente,
                search: filters.search,
                fechaInicio: filters.fechaInicio,
                fechaFin: filters.fechaFin
            };

            const response = await axios.get(`${API_URL}/api/v2/pagos`, { ...config, params });
            // El backend V2 devuelve { total, items, page, limit } para paginacion
            // O un array si es endpoint legacy (pero v2 es paginado).
            // Para compatibilidad con Pagos.jsx actual que espera array directo en fetchPagos(),
            // quizás necesitemos retornar response.data.items si el front no maneja paginación.
            // Revisando Pagos.jsx legacy: `setPagos(response.data.sort...)` <- espera array.
            // Para mantener compatibilidad rápida, retornaremos el array de items si viene paginado.
            return response.data;
        } catch (error) {
            console.error('Error fetching historial pagos:', error);
            throw error.response?.data?.error || 'Error al obtener historial de pagos';
        }
    }

    /**
     * Consultar Deuda (Estado de Cuenta)
     * Endpoint: GET /api/v2/pagos/consultar-deuda
     * Params: codigoMedidor o cedula
     */
    static async consultarDeuda(criterio) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            const params = {};
            // Detectar si es cedula o medidor (simple heuristica o parametro explicito)
            // Asumimos que criterio puede ser cualquiera.
            // El backend acepta query params: codigoMedidor, cedula.
            // Intentamos enviar ambos si no estamos seguros, o detectamos longitud.
            if (criterio.length > 7 && !criterio.includes('-') && /^\d+$/.test(criterio)) {
                params.cedula = criterio;
            } else if (/^\d+$/.test(criterio)) {
                params.codigoMedidor = criterio;
            } else {
                params.nombre = criterio;
            }

            const response = await axios.get(`${API_URL}/api/v2/pagos/consultar-deuda`, { ...config, params });
            return response.data; // Retorna array de objetos "EstadoCuenta"
        } catch (error) {
            console.error('Error fetching estado cuenta:', error);
            throw error.response?.data?.error || 'Error al consultar deuda';
        }
    }

    /**
     * Registrar Nuevo Pago
     * Endpoint: POST /api/v2/pagos
     */
    static async registrarPago(pagoData) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            /* pagoData esperado por backend V2:
                {
                    id_medidor, 
                    id_consumo,
                    numero_comprobante,
                    estado_pago,
                    fecha_pago (opcional)
                }
            */
            const payload = {
                id_medidor: pagoData.id_medidor,
                id_consumo: pagoData.id_consumo,
                id_deuda_detalle: pagoData.id_deuda_detalle,
                monto_pago: pagoData.monto_pago,
                deudasExtrasSeleccionadas: pagoData.deudasExtrasSeleccionadas || [], // New
                numero_comprobante: pagoData.numero_comprobante || Math.floor(Math.random() * 100000).toString(),
                estado_pago: 'Pagado',
                fecha_pago: pagoData.fecha_pago
            };

            const response = await axios.post(`${API_URL}/api/v2/pagos`, payload, config);
            return response.data;
        } catch (error) {
            console.error('Error registering pago:', error);
            throw error.response?.data?.error || 'Error al registrar pago';
        }
    }

    /**
     * Anular Pago
     * Endpoint: DELETE /api/v2/pagos/:id
     */
    static async anularPago(id) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            await axios.delete(`${API_URL}/api/v2/pagos/${id}`, config);
            return true;
        } catch (error) {
            console.error('Error anular pago:', error);
            throw error.response?.data?.error || 'Error al anular pago';
        }
    }

    /**
     * Obtener suma total recaudada en un día específico
     * Endpoint: GET /api/v2/pagos/suma-total
     */
    static async getRecaudacionDia(fecha) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const response = await axios.get(`${API_URL}/api/v2/pagos/suma-total`, {
                ...config,
                params: { fecha }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching recaudacion dia:', error);
            throw error.response?.data?.error || 'Error al obtener recaudación del día';
        }
    }

    static async getResumenMensual(startDate, endDate) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const response = await axios.get(`${API_URL}/api/v2/pagos/resumen-mensual`, {
                ...config,
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching resumen mensual:', error);
            throw error.response?.data?.error || 'Error al obtener resumen mensual';
        }
    }
}
